// filepath: apps/web/src/features/promote/NeedsReviewViewer.tsx
//
// FR-UI-PROM-2 AC-3 — renders the NEEDS_REVIEW / verified envelope
// for agent-authored submissions.
//
// INV-9 compliant: the spec body, bpm_step_log_snapshot, and
// terminal_failure_reason are all rendered as text nodes only.
// The <pre> blocks wrap JSON.stringify output, NOT raw HTML —
// React's JSX text-child escaping keeps server-supplied strings
// inert against XSS payloads.
//
// INV-5: secret values are NEVER rendered. The artifact envelope
// may carry reference-style secret ids (FR-SCR-8); the viewer
// renders those ids verbatim via text nodes. Concrete secret
// values would leak through the underlying JSON.stringify call —
// the viewer's surface intentionally treats the spec as opaque
// text and does not extract, transform, or interpret any field.
//
// Deep-link: a button links to
// GET /api/staging/agent-artifacts/{artifact_id} (FR-AGT-4 surface)
// for forensic drill-down. The link uses `target="_blank"` +
// `rel="noopener noreferrer"` per a11y conventions.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type { ArtifactEnvelope } from "./types";

interface NeedsReviewViewerProps {
  artifact: ArtifactEnvelope | null;
}

export function NeedsReviewViewer({
  artifact,
}: NeedsReviewViewerProps): React.ReactElement | null {
  const { t } = useTranslation();

  if (!artifact) {
    return (
      <p
        className="text-sm text-muted-foreground"
        data-testid="needs-review-viewer-empty"
        aria-live="polite"
      >
        {t("promote.gate.artifact.none")}
      </p>
    );
  }

  const deepLink = `/api/staging/agent-artifacts/${encodeURIComponent(
    artifact.artifact_id,
  )}`;

  return (
    <article
      aria-label={t("promote.gate.section.artifact")}
      className="space-y-4"
      data-testid="needs-review-viewer"
    >
      {/* Envelope metadata */}
      <section aria-labelledby="envelope-meta" className="space-y-1">
        <h3 id="envelope-meta" className="text-sm font-semibold">
          {t("promote.gate.artifact.envelope_meta")}
        </h3>
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
          <MetaTerm>{t("promote.gate.artifact.task_spec_id")}</MetaTerm>
          <MetaValue testId="envelope-task-spec-id">
            {artifact.task_spec_id}
          </MetaValue>
          <MetaTerm>{t("promote.gate.artifact.task_spec_version")}</MetaTerm>
          <MetaValue testId="envelope-task-spec-version">
            {artifact.task_spec_version}
          </MetaValue>
          <MetaTerm>{t("promote.gate.artifact.kind")}</MetaTerm>
          <MetaValue testId="envelope-kind">{artifact.kind}</MetaValue>
          <MetaTerm>{t("promote.gate.artifact.agent_principal")}</MetaTerm>
          <MetaValue testId="envelope-agent-principal">
            {artifact.agent_principal}
          </MetaValue>
          <MetaTerm>{t("promote.gate.artifact.submitted_at")}</MetaTerm>
          <MetaValue testId="envelope-submitted-at">
            {artifact.submitted_at}
          </MetaValue>
          <MetaTerm>{t("promote.gate.artifact.attempt_count")}</MetaTerm>
          <MetaValue testId="envelope-attempt-count">
            {artifact.attempt_count} / {artifact.retry_budget}
          </MetaValue>
          <MetaTerm>{t("promote.gate.artifact.spec_hash")}</MetaTerm>
          <MetaValue testId="envelope-spec-hash" mono>
            {artifact.spec_hash}
          </MetaValue>
          <MetaTerm>{t("promote.gate.artifact.final_status")}</MetaTerm>
          <MetaValue testId="envelope-final-status">
            {artifact.final_status}
          </MetaValue>
        </dl>
      </section>

      {/* Spec body */}
      <section aria-labelledby="envelope-spec" className="space-y-1">
        <h3 id="envelope-spec" className="text-sm font-semibold">
          {t("promote.gate.artifact.spec")}
        </h3>
        <pre
          aria-label={t("promote.gate.artifact.spec")}
          className="rounded bg-muted p-2 text-xs font-mono whitespace-pre-wrap break-all max-h-64 overflow-auto"
          data-testid="envelope-spec-body"
        >
          {stringifySafe(artifact.spec)}
        </pre>
      </section>

      {/* Per-attempt sections */}
      <section
        aria-labelledby="envelope-attempts"
        className="space-y-2"
        data-testid="envelope-attempts"
      >
        <h3 id="envelope-attempts" className="text-sm font-semibold">
          {t("promote.gate.artifact.attempts")}
        </h3>
        <ol className="space-y-2">
          {(artifact.attempts ?? []).map((attempt) => (
            <li
              key={attempt.attempt_number}
              className="rounded border border-border p-2 text-xs"
              data-testid={`envelope-attempt-${attempt.attempt_number}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium">
                  attempt {attempt.attempt_number}
                </span>
                <span className="font-mono text-muted-foreground">
                  sandbox {attempt.sandbox_uuid}
                </span>
              </div>
              <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                <MetaTerm>{t("promote.gate.artifact.started_at")}</MetaTerm>
                <MetaValue testId={`attempt-${attempt.attempt_number}-started`}>
                  {attempt.started_at}
                </MetaValue>
                <MetaTerm>{t("promote.gate.artifact.completed_at")}</MetaTerm>
                <MetaValue
                  testId={`attempt-${attempt.attempt_number}-completed`}
                >
                  {attempt.completed_at}
                </MetaValue>
                <MetaTerm>
                  {t("promote.gate.artifact.terminal_failure_reason")}
                </MetaTerm>
                <MetaValue
                  testId={`attempt-${attempt.attempt_number}-failure`}
                  mono
                >
                  {attempt.terminal_failure_reason || "—"}
                </MetaValue>
              </dl>
              {attempt.bpm_step_log_snapshot &&
                attempt.bpm_step_log_snapshot.length > 0 && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-muted-foreground">
                      {t("promote.gate.artifact.bpm_step_log_snapshot")}
                    </summary>
                    <pre
                      aria-label={t(
                        "promote.gate.artifact.bpm_step_log_snapshot",
                      )}
                      className="mt-1 rounded bg-muted p-2 font-mono whitespace-pre-wrap break-all max-h-48 overflow-auto"
                    >
                      {stringifySafe(attempt.bpm_step_log_snapshot)}
                    </pre>
                  </details>
                )}
            </li>
          ))}
        </ol>
      </section>

      {/* Deep-link to the full envelope */}
      <section>
        <a
          href={deepLink}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-primary underline"
          data-testid="envelope-deep-link"
        >
          {t("promote.gate.artifact.open_full_envelope")}
        </a>
      </section>
    </article>
  );
}

function MetaTerm({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return <dt className="text-muted-foreground">{children}</dt>;
}

function MetaValue({
  children,
  testId,
  mono,
}: {
  children: React.ReactNode;
  testId?: string;
  mono?: boolean;
}): React.ReactElement {
  return (
    <dd
      className={mono ? "font-mono break-all" : undefined}
      data-testid={testId}
    >
      {children}
    </dd>
  );
}

function stringifySafe(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return "[unserializable]";
  }
}
